import { Box, Heading2, Heading3, Span, StackView, Text } from 'localmed-core';
import React, { useContext, useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { DEPENDENT_STATUS } from '../../../shared/components/AppointmentForm/constants';
import { DiButton, Link, RadioGroup } from '../../../shared/components/DiComponents';
import { DiInputAdapter } from '../../../shared/components/FinalForm';
import {
  DiPatientScreener,
  PatientScreenerFailure,
  PatientScreenerSuccess,
  SCREENER_STATUSES,
} from '../../../shared/components/PatientScreener';
import { SchedulingSessionStateContext } from '../../../shared/components/SchedulingSessionState';
import { IStepProps } from '../../pages/SchedulingPage/components/schedulingWizard';
import {
  CheckBox,
  ContentWrapper,
  Label,
  Wrapper,
} from '../../pages/SchedulingPage/components/styles';
import { shouldSkipReasonSelectionScreen } from '../../pages/SchedulingPage/utils';
import InputError from '../InputError';
import PatientStatusOption from './PatientStatusOption';

interface IProps extends IStepProps {
  hasPassedInitialScreening?: boolean;
}

export default function InitialScreen({ wizard, hasPassedInitialScreening }: IProps) {
  const [showScreenerSuccess, setShowScreenerSuccess] = useState(false);
  const { screenerStatus } = useContext(SchedulingSessionStateContext);
  useEffect(() => {
    if (screenerStatus === SCREENER_STATUSES.PASSED) {
      setShowScreenerSuccess(true);
    }
    if (wizard?.trackSchedulingEvent) {
      wizard.trackSchedulingEvent('Initial Scheduling Screen', 'InitialScreen');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!wizard) return null;

  const {
    office,
    provider,
    patientGivenName,
    patientFamilyName,
    patientStatuses,
    patientStatus,
    bookingForAnother,
    reasonsForVisit,
  } = wizard.values;

  const showScheduling =
    screenerStatus === SCREENER_STATUSES.PASSED || !office?.patientScreenerEnabled;

  if (!showScheduling && screenerStatus === SCREENER_STATUSES.FAILED) {
    return <PatientScreenerFailure phoneNumber={office?.phone} />;
  }

  if (reasonsForVisit?.length === 0 || !office?.onlineSchedulingEnabled) {
    return (
      <Wrapper>
        <ContentWrapper>
          <Box width="100%" textAlign={{ _: 'center', lg: 'left' }}>
            <Heading3>
              <FormattedMessage
                id="scheduling.InitialScreen.noOnlineSchedulingMessage"
                defaultMessage="We’re not accepting appointments online at the moment."
              />
            </Heading3>
            <Text>
              <FormattedMessage
                id="scheduling.InitialScreen.noOnlineSchedulingMessage"
                defaultMessage="Please call us at {link} to schedule your appointment."
                values={{
                  link: (
                    <Span fontWeight="bold">
                      <Link href={`tel:${office?.phone}`} title="phone number">
                        {office?.phone}
                      </Link>
                    </Span>
                  ),
                }}
              />
            </Text>
          </Box>
        </ContentWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ContentWrapper>
        {showScheduling ? (
          <Box width="100%" textAlign={{ _: 'center', lg: 'left' }}>
            {showScreenerSuccess && !hasPassedInitialScreening && (
              <PatientScreenerSuccess onClose={setShowScreenerSuccess} />
            )}
            <StackView spacing="lg">
              <Heading2>
                <FormattedMessage
                  id="scheduling.InitialScreen.bookingMessage"
                  defaultMessage="Hi, Welcome to {officeDisplay}"
                  values={{
                    officeDisplay: office?.display,
                  }}
                />
              </Heading2>
              <StackView spacing="sm">
                <Text mb="xs" textAlign={{ _: 'center', lg: 'initial' }}>
                  <FormattedMessage
                    id="scheduling.InitialScreen.bookingMessage"
                    defaultMessage="Who is this appointment for?"
                  />
                </Text>
                <Box textAlign="left">
                  <Label id="name" htmlFor="firstName" mb="xs" required>
                    <FormattedMessage
                      id="scheduling.InitialScreen.patientNameInputsLabel"
                      defaultMessage="Patient Name (Required)"
                    />
                  </Label>
                  <StackView spacing="sm" direction="row">
                    <Field
                      name="patientGivenName"
                      flex="1 0 0"
                      id="firstName"
                      placeholder="First Name"
                      autoComplete="given-name"
                      component={DiInputAdapter}
                      aria-labelledby="name"
                    />

                    <Field
                      name="patientFamilyName"
                      flex="1 0 0"
                      id="lastName"
                      placeholder="Last Name"
                      autoComplete="family-name"
                      component={DiInputAdapter}
                      aria-labelledby="name"
                    />
                  </StackView>
                  <InputError form={wizard.form} field="patientGivenName" />
                  <InputError form={wizard.form} field="patientFamilyName" />
                </Box>
                <Box textAlign="left">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="other">
                    <CheckBox
                      type="checkbox"
                      id="other"
                      name="other"
                      checked={bookingForAnother}
                      onChange={(event) => {
                        wizard.form.batch(() => {
                          wizard.form.change('bookingForAnother', !!event.target.checked);
                          wizard.form.change(
                            'dependentStatus',
                            event.target.checked ? DEPENDENT_STATUS.DEPENDENT : DEPENDENT_STATUS.ME
                          );
                        });
                      }}
                    />
                    <FormattedMessage
                      id="scheduling.InitialScreen.scheduleForOtherLabelMessage"
                      defaultMessage="I’m booking for someone else"
                    />
                  </label>
                </Box>
              </StackView>
            </StackView>

            <StackView spacing="lg" mt="xl">
              <Label
                htmlFor="patientStatuses"
                fontWeight="normal"
                fontSize="md"
                textAlign={{ _: 'center', lg: 'initial' }}
              >
                <FormattedMessage
                  id="scheduling.InitialScreen.bookingMessage"
                  defaultMessage="{bookingForAnother, select, true {Have they visited us before?} false {Have you visited us before?}}"
                  values={{
                    bookingForAnother,
                  }}
                />
              </Label>
              <RadioGroup
                mt="sm"
                id="patientStatuses"
                name="patientStatuses"
                items={patientStatuses || []}
                itemDisplayComponent={PatientStatusOption}
                onChange={(value) => {
                  wizard.form.batch(() => {
                    wizard.form.change('patientStatus', value);
                    const { reasonForVisit } = wizard.values;
                    const filteredReasons = (reasonsForVisit || []).filter(
                      (reason) => reason.patientStatus === value?.id
                    );
                    if (filteredReasons.length === 1) {
                      const newReason = filteredReasons[0];
                      if (reasonForVisit == null || reasonForVisit.id !== newReason.id) {
                        wizard.form.change('reasonForVisit', newReason);
                        return;
                      }
                    }
                    if (reasonForVisit && reasonForVisit.patientStatus !== value) {
                      wizard.form.change('reasonForVisit', null);
                    }
                  });
                }}
                value={patientStatus}
                itemWrapperProps={{
                  flex: '1 0 0',
                }}
              />
            </StackView>
            <Box width="100%" display="flex" justifyContent={{ _: 'center', lg: 'flex-end' }}>
              <DiButton
                aria-label="Next"
                tabIndex={0}
                onClick={() => {
                  if (shouldSkipReasonSelectionScreen(wizard)) {
                    // Skip directly to openings if reason is already selected via query param
                    wizard.gotoStep(2);
                  } else {
                    wizard.gotoNextStep();
                  }
                }}
                size="xl"
                mt="lg"
                disabled={!patientGivenName || !patientFamilyName || !patientStatus?.id}
              >
                <FormattedMessage id="scheduling.InitialScreen.nextButton" defaultMessage="Next" />
              </DiButton>
            </Box>
          </Box>
        ) : (
          <>
            {screenerStatus === SCREENER_STATUSES.NOT_SCREENED && (
              <DiPatientScreener
                officeId={office?.id}
                officeDisplay={office?.display}
                providerId={provider?.id}
                providerDisplay={provider?.display}
              />
            )}
          </>
        )}
      </ContentWrapper>
    </Wrapper>
  );
}

InitialScreen.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  id: 'INITIAL_SCREEN',
};
